import { lazy } from "react";
import type { RouteConfig } from "../config";
import { ROUTES } from "../paths";

const AttendanceSharePage = lazy(() =>
  import("@/pages/public/attendance-share-page").then((m) => ({
    default: m.AttendanceSharePage,
  }))
);
const JobApplyPage = lazy(() =>
  import("@/pages/public/job-apply-page").then((m) => ({
    default: m.JobApplyPage,
  }))
);
const CandidaturasListPage = lazy(() =>
  import("@/pages/public/candidaturas-list-page").then((m) => ({
    default: m.CandidaturasListPage,
  }))
);
const CandidaturaStatusPage = lazy(() =>
  import("@/pages/public/candidatura-status-page").then((m) => ({
    default: m.CandidaturaStatusPage,
  }))
);
const DashboardPage = lazy(() =>
  import("@/pages/dashboard").then((m) => ({ default: m.DashboardPage }))
);
const BarcodesPage = lazy(() =>
  import("@/pages/barcodes").then((m) => ({ default: m.BarcodesPage }))
);
const LeadsPage = lazy(() =>
  import("@/pages/leads").then((m) => ({ default: m.LeadsPage }))
);
const VacationRequestsPage = lazy(() =>
  import("@/pages/vacation-requests-page").then((m) => ({
    default: m.VacationRequestsPage,
  }))
);
const OrdersPage = lazy(() =>
  import("@/pages/orders").then((m) => ({ default: m.OrdersPage }))
);
const NewOrderRedirect = lazy(() =>
  import("@/pages/orders").then((m) => ({
    default: m.NewOrderRedirect,
  }))
);
const ImportPdfOrderPage = lazy(() =>
  import("@/pages/clients/orders/order-import-pdf-page").then((m) => ({
    default: m.ImportPdfOrderPage,
  }))
);

export const publicRoutes: RouteConfig[] = [
  // Public (no auth)
  {
    path: ROUTES.ATTENDANCE_SHARE(":token"),
    component: AttendanceSharePage,
    layout: undefined,
    permissions: { access: "public" },
  },
  // Recrutamento público — feature dentro de Recursos Humanos.
  //
  // Abertas a quem não tem conta (é o objectivo: quem se candidata ainda não é
  // colaborador), mas só existem no cliente que recruta pela plataforma. Sem a
  // feature, um cliente sem recrutamento serviria uma página de candidaturas
  // vazia com a sua marca — e o endereço é adivinhável.
  {
    path: ROUTES.JOBS_LIST,
    component: CandidaturasListPage,
    layout: undefined,
    permissions: { access: "public", feature: "hr.recrutamento_publico" as const },
  },
  {
    path: ROUTES.APPLY_PARAM,
    component: JobApplyPage,
    layout: undefined,
    permissions: { access: "public", feature: "hr.recrutamento_publico" as const },
  },
  {
    path: ROUTES.CANDIDATURA_STATUS,
    component: CandidaturaStatusPage,
    layout: undefined,
    permissions: { access: "public", feature: "hr.recrutamento_publico" as const },
  },

  // Dashboard — any authenticated user
  {
    path: ROUTES.DASHBOARD,
    component: DashboardPage,
    layout: "dashboard",
    permissions: { access: "authenticated" },
  },

  // Rótulos — feature dentro do módulo de Ordens de Fabrico.
  //
  // Não basta esconder no menu: quem escreve o endereço entrava à mesma. A
  // `feature` no guarda de rotas é que fecha a página a quem não a tem.
  //
  // ⚠️ Dizia `artigos.rotulos`, que NÃO existe no catálogo — a feature mudou de
  // módulo quando `ordens_fabrico` se separou de `artigos` e este id ficou para
  // trás. `hasFeature` devolve False para o que não conhece, por isso a página
  // estava fechada a TODA a gente, incluindo quem a comprou. O `features` do
  // perfil é `string[]` no frontend: um id mal escrito não falha a compilação.
  {
    path: ROUTES.BARCODES,
    component: BarcodesPage,
    layout: "dashboard",
    permissions: { access: "permission", entity: "productions", action: "READ", feature: "ordens_fabrico.rotulos" as const },
  },

  // Leads — entidade e feature têm de bater com o gate do backend
  // (`require_admin_or_permission("leads", "READ")` + `require_feature("comercial.leads")`
  // no `main.py`). Estava a pedir `clients.READ` sem feature: quem tivesse
  // acesso a Clientes mas não ao CRM de oportunidades entrava numa página
  // vazia em vez de ser barrado — achado do auditor de permissões, 2026-09-07.
  {
    path: ROUTES.LEADS,
    component: LeadsPage,
    layout: "dashboard",
    permissions: { access: "permission", entity: "leads", action: "READ", feature: "comercial.leads" as const },
  },

  // Vacation Requests
  {
    path: ROUTES.VACATION_REQUESTS,
    component: VacationRequestsPage,
    layout: "dashboard",
    permissions: { access: "authenticated" },
  },



  // Global Orders
  {
    path: ROUTES.ORDERS.LIST,
    component: OrdersPage,
    layout: "dashboard",
    permissions: { access: "permission", entity: "orders", action: "READ" },
  },
  {
    path: ROUTES.ORDERS.CREATE,
    component: NewOrderRedirect,
    layout: "dashboard",
    permissions: { access: "permission", entity: "orders", action: "CREATE" },
  },
  {
    path: ROUTES.ORDERS.IMPORT_PDF,
    component: ImportPdfOrderPage,
    layout: "dashboard",
    permissions: { access: "permission", entity: "orders", action: "CREATE" },
  },
];
